import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Order, Address, LexwareContact } from 'src/app/interfaces/orders';
import { AlertService } from 'src/app/services/base/alert.service';
import { OfflineService } from 'src/app/services/manager/offline.service';
import { ModalService } from 'src/app/services/base/modal.service';
import { ChangeAddressComponent } from '../../components/order/change-address/change-address.component';
import { LexwareContactComponent } from '../../components/contact/lexware-contact/lexware-contact.component';
import { SubscriberService } from './subscriber.service';
import { DataServerService } from '../api/data-server.service';
import { OrderService } from './order.service';
import { CoreService } from '../manager/core.service';
import { LoginService } from 'src/app/services/auth/login.service';
import { App } from '@capacitor/app';

/**
 * Einheitlicher Hinweis, wenn an einem abgeschlossenen Auftrag etwas geändert
 * werden soll. Bewusst an einer Stelle definiert, damit Service und Components
 * dieselbe Meldung zeigen.
 */
export const ORDER_CLOSED_MESSAGE =
  'Auftrag ist abgeschlossen. Für Änderungen bitte um Freigabe bitten.';

@Injectable({
  providedIn: 'root',
})
export class CurrentOrderService {
  destroy = new Subject();

  // Sicherheitsnetz: wenn seit der letzten Änderung 3 Minuten lang nichts mehr passiert ist
  // (weder Speichern noch Verlassen), wird automatisch ein Server-Sync-Versuch angestoßen —
  // falls der Nutzer schlicht vergisst zu speichern.
  private inactivityTimer: any = null;
  private readonly inactivityTimeoutMs = 3 * 60_000;

  constructor(
    private subscriberService: SubscriberService,
    private alert: AlertService,
    private offline: OfflineService,
    private modals: ModalService,
    private dataServer: DataServerService,
    private order: OrderService,
    private core: CoreService,
    private login: LoginService
  ) {
    this.registerLifecycleFlushListeners();
  }

  /**
   * Kanonische Regel dafür, ob an den Produkten eines Auftrags noch etwas
   * geändert werden darf. Einzige Quelle für diese Entscheidung — Components
   * sollen sie nicht erneut implementieren, sondern hierher delegieren.
   *
   * Ein abgeschlossener Auftrag (state === 1) friert in finishOrder() die
   * productsOriginal ein; nachträgliche Produktänderungen würden davon
   * abweichen und Lieferschein, Rechnung und Lexware-Übertragung verfälschen.
   *
   * Admins dürfen bewusst auch abgeschlossene Aufträge bearbeiten — analog zu
   * showStandardActions() in der current-order.component.
   */
  canModifyProducts(order?: Order | null): boolean {
    const current = order ?? this.subscriberService.currentOrder.getValue();
    if (!current) {
      return false;
    }

    // Noch nicht gespeicherter Auftrag: hat weder Owner noch state.
    if (current.owner === 'NEW') {
      return true;
    }

    if (this.login.decodedToken?.role === 'admin') {
      return true;
    }

    const owner = current.owner as any;
    const ownerEmail = typeof owner === 'string' ? owner : owner?.email;
    const isOwner = !!ownerEmail && ownerEmail === this.login.decodedToken?.email;

    // state kann null/undefined sein (Altbestand, neu angelegt) — das gilt als offen.
    const raw = (current as any).state;
    const state = raw === null || raw === undefined || raw === '' ? null : Number(raw);
    const isOpen = state === null || Number.isNaN(state) || state === 0;

    return isOwner && isOpen;
  }

  /**
   * Registriert Signale für "App/Tab verschwindet gerade" und flusht dann sofort den
   * aktuellen Bearbeitungsstand. Drei unabhängige Signale, da kein einzelnes auf allen
   * Plattformen zuverlässig ist: appStateChange (nativ, Hintergrund/Minimieren),
   * pagehide (Browser: Tab schließen/Reload/Navigation weg), visibilitychange
   * (Browser: Tab-Wechsel/Minimieren, Fallback).
   */
  private registerLifecycleFlushListeners(): void {
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        this.flushOnLifecycleSignal();
      }
    });

    window.addEventListener('pagehide', () => {
      this.flushOnLifecycleSignal();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushOnLifecycleSignal();
      }
    });
  }

  private flushOnLifecycleSignal(): void {
    this.stopInactivityTimer();
    const order = this.subscriberService.currentOrder.getValue();
    if (!order) return;

    // Lokale Persistenz ist die eigentliche Garantie — Sync-Versuch ist Best-Effort,
    // da bei pagehide/Hintergrund-Wechsel oft keine Zeit mehr für einen Netzwerk-Request bleibt.
    this.offline.setCurrentOrder(order, true, true);
    this.offline.saveOrderRevision(order); // Meilenstein: App/Tab verschwindet gerade
    void this.order.syncOfflineOrders(true);
  }

  // Aktuelles Order-Observable vom SubscriberService
  get currentOrder$() {
    return this.subscriberService.currentOrder;
  }

  // Methode zum Speichern der Bestellung
  async saveOrder() {
    this.flushPresave();
    const order = this.subscriberService.currentOrder.getValue();
    if(order) {
      // Check if order is closed and requires change reason
      if (order.state === 1) {
        const reason = await this.alert.presentTextInputAlert(
          'Änderungsbegründung erforderlich',
          'Dieser Auftrag wurde bereits abgeschlossen. Bitte begründen Sie die Änderung:',
          'Begründung eingeben...'
        );

        if (!reason || reason.trim() === '') {
          this.alert.presentToast('Speichern abgebrochen - keine Begründung angegeben');
          return;
        }

        // Add change reason as comment via API
        try {
          await this.dataServer.addOrderComment(order.orderId, reason, true);
        } catch (e) {
          console.error('Failed to add change reason comment:', e);
        }
      }

      const save = await this.offline.setCurrentOrder(order, true, true);
      this.offline.saveOrderRevision(order); // Meilenstein: expliziter Speichern-Button
      this.offline.resetCurrentOrder();
    }
  }

  private sanitizeContact(contact: any): LexwareContact {
    return {
      id: contact.id,
      owner: typeof contact.owner === 'string' ? contact.owner : contact.owner?.id,
      companyName: contact.companyName || '',
      email: contact.email,
      phone: contact.phone,
      website: contact.website,
      contactPerson: contact.contactPerson,
      address: contact.address,
      delivery: contact.delivery,
      note: contact.note,
      deactivated: contact.deactivated,
      kuerzel: contact.kuerzel,
      contactId: contact.contactId,
    };
  }

  // Lexware-Kontakt direkt setzen (z.B. aus Inline-Suche)
  setLexwareContact(contact: any) {
    const order = this.subscriberService.currentOrder.getValue();
    if (!order || !contact) {
      return;
    }

    order.customerAddress = null;
    order.customerBase = this.sanitizeContact(contact);
    void this.resyncCustomerAddress(order);
  }

  /**
   * Speichert den aktuellen Stand sofort lokal (Datenverlust-Schutz) — OHNE Revision.
   * Wird bei jeder einzelnen Order-Änderung aufgerufen (über updateOrder()), daher darf
   * hier keine Revision entstehen — sonst wächst der Verlauf bei jedem Klick, nicht nur
   * bei echten Meilensteinen (Speichern-Button, Verlassen, Sync, App-Hintergrund).
   */
  async presaveOrder() {
    const order = this.subscriberService.currentOrder.getValue();
    if(order) {
      await this.offline.setCurrentOrder(order, true, true);
    }
  }

  // Methode zum Verlassen der Bestellung
  async resetAndExit() {
    // Kein flushPresave() hier: presaveOrder() markiert die Order als "ungesynct" und
    // würde dadurch einen Server-Sync auslösen — genau das soll "Verlassen ohne Speichern"
    // verhindern. Nur den Inaktivitäts-Timer stoppen, nichts lokal persistieren.
    this.stopInactivityTimer();
    const result = await this.alert.presentConfirmationAlert(
      'Verlassen',
      'Alle ungespeicherten Änderungen gehen verloren. Fortfahren?'
    );

    if (result) {
      this.offline.resetCurrentOrder();
    }
  }

  async forceExit() {
    // Wie resetAndExit(): kein presaveOrder(), damit verworfene Änderungen keinen Sync auslösen.
    this.stopInactivityTimer();
    this.offline.resetCurrentOrder();
  }

  async saveAndExit(shouldSave: boolean = true) {
    this.flushPresave();
    const order = this.subscriberService.currentOrder.getValue();
    if(order && shouldSave) {
      const save = await this.offline.setCurrentOrder(order, true, true);
      this.offline.saveOrderRevision(order); // Meilenstein: Speichern beim Verlassen
      this.offline.resetCurrentOrder();

      //TODO: Navigate to another page
      await this.order.syncOfflineOrders(false, 'saveAndExit')
      this.core.refreshOrders();
    } else if (order) {
      // Nur zurücksetzen ohne zu speichern — kein Sync/Refresh bei verworfenen Änderungen
      this.offline.resetCurrentOrder();
    }
  }

  async justSave() {
    this.flushPresave();
    const order = this.subscriberService.currentOrder.getValue();
    if (order) {
      await this.offline.setCurrentOrder(order, true, true);
      this.offline.saveOrderRevision(order); // Meilenstein: expliziter Speichern-Button
    }

    // "Lokal gespeichert" ist hier die einzig ehrliche Aussage — der Server-Sync
    // läuft asynchron über den Retry-Loop und kann fehlschlagen, ohne dass justSave() das weiß.
    this.alert.presentToast('Lokal gespeichert.');
  }



  // Lexware-Kontakt wählen
  async chooseLexwareContact() {
    const order = this.subscriberService.currentOrder.getValue();
    const result = await this.modals.openModal(LexwareContactComponent, 1);

    if (order && result) {
      order.customerAddress = null;
      order.customerBase = this.sanitizeContact(result);
      await this.resyncCustomerAddress(order);
    }
  }

  // Adresse ändern
  async changeAddress() {
    const order = this.subscriberService.currentOrder.getValue();
    const result = await this.modals.openModal(ChangeAddressComponent, {
      data: order?.customerAddress,
      customerBase: order?.customerBase,
    });

    if (order && result) {
      // Update customer address
      if (result.address) {
        order.customerAddress = result.address;
      }

      // Update delivery address in customerBase
      if (result.deliveryAddress && order.customerBase) {
        order.customerBase.delivery = result.deliveryAddress;
      }

      await this.updateOrder(order);
    }
  }

  // Kundendaten synchronisieren
  async resyncCustomerAddress(order: any) {
    if (!order?.customerBase || order?.customerAddress) {
      return;
    }

    // Handle the new contact payload structure
    const customerBase = order.customerBase;

    // Create address object from the new payload structure
    const address: Address = {
      name: customerBase.companyName || '',
      supplement: customerBase.address?.supplement || '',
      street: customerBase.address?.street || '',
      city: customerBase.address?.city || '',
      zip: customerBase.address?.zip || '',
      countryCode: 'DE', // Default to DE
    };

    // Set contactId if available
    if (customerBase.id) {
      address.contactId = customerBase.id.toString();
    }

    // Set customer email from the contact person or main email
    if (customerBase.contactPerson?.emailAddress) {
      order.customerEmail = customerBase.contactPerson.emailAddress;
    } else if (customerBase.email) {
      order.customerEmail = customerBase.email;
    }

    order.customerAddress = address;
    await this.updateOrder(order);
  }

  // Kontakt zurücksetzen
  async resetContact() {
    const order = this.subscriberService.currentOrder.getValue();
    if (order) {
      order.customerBase = null;
      order.customerAddress = null;
      await this.updateOrder(order);
    }
  }

  addOrUpdateProducts(newProducts: any[], save: boolean = true) {
    const order = this.subscriberService.currentOrder.getValue();
    if (!order) {
      console.error('No current order available.');
      return;
    }

    // Zentraler Schutz: hier laufen alle Produkt-Pfade zusammen (Produkt-Modal,
    // Schnellmengen, Inline-Suche, Produktliste). Die UI-Guards davor sind nur
    // Komfort — verhindert wird die Änderung erst an dieser Stelle.
    if (!this.canModifyProducts(order)) {
      this.alert.presentToast(ORDER_CLOSED_MESSAGE);
      return;
    }

    if (!order.products) {
      // Wenn keine Produkte existieren, ein neues Array erstellen
      order.products = [...newProducts];
    } else {
      // Bestehende Produkte aktualisieren oder hinzufügen
      newProducts.forEach((newProduct) => {
        const existingProductIndex = order.products.findIndex(
          (product: any) => product.productId === newProduct.productId
        );

        if (existingProductIndex !== -1) {
          // Überschneidungen: Produkt aktualisieren
          const existingProduct = order.products[existingProductIndex];

          // Aktualisierung der Größen und anderer Felder
          existingProduct.sizes = this.mergeSizes(
            existingProduct.sizes,
            newProduct.sizes
          );

          // Andere relevante Felder überschreiben
          Object.assign(existingProduct, newProduct);
        } else {
          // Neues Produkt hinzufügen
          order.products.push(newProduct);
        }
      });
    }

    // Produkte ohne Stückzahlen entfernen, außer sie haben "blocked" oder "info"
    order.products = order.products.filter((product: any) => {
      const hasQuantities = product.sizes.some(
        (size: any) => size.quantity > 0
      );
      const hasBlockedOrInfo = product.blocked || product.info;
      return hasQuantities || hasBlockedOrInfo;
    });

    // Produkte nach dem `model`-Wert sortieren
    order.products.sort((a: any, b: any) => {
      if (a.model < b.model) return -1;
      if (a.model > b.model) return 1;
      return 0;
    });

    // Aktualisierte Bestellung propagieren

    if (save) {
      this.updateOrder(order);
    }else{
      this.subscriberService.setCurrentOrder(order);
    }

  }

  private mergeSizes(existingSizes: any[], newSizes: any[]): any[] {
    const sizeMap = new Map();

    // Bestehende Größen in die Map einfügen
    existingSizes.forEach((size) => {
      sizeMap.set(size.size, { ...size });
    });

    // Neue Größen hinzufügen/aktualisieren
    newSizes.forEach((size) => {
      sizeMap.set(size.size, { ...size });
    });

    // In ein Array umwandeln
    return Array.from(sizeMap.values());
  }

  async changeTitle() {
    const order = this.subscriberService.currentOrder.getValue();
    if (order) {
      const response = await this.alert.presentTextInputAlert(
        'Titel ändern',
        'Ttiel eingeben:',
        order.orderTitle || '',
        'Speichern',
        'Abbrechen'
      );

      if (response !== null) {
        order.orderTitle = response;
        await this.updateOrder(order);
      }
    }
  }

  async archiveOrdner() {
    const order = this.subscriberService.currentOrder.getValue();
    if (order) {
      const response = await this.alert.presentConfirmationAlert(
        'Archivieren',
        'Möchten Sie den Auftrag wirklich verbergen?'
      );

      if (response) {
        const response2 = await this.alert.presentConfirmationAlert(
          'WARNUNG',
          'DIE BESTELLUNG WIRD NICHT MEHR SICHTBAR SEIN! WIRKLICHFORTFAHREN?'
        );
        if (response2) {
          order.status = 1;
          await this.updateOrder(order);
          // Ein verborgener Auftrag darf nicht offen bleiben — analog zu finishOrder()
          // speichern und schließen, sonst bearbeitet man weiter etwas Unsichtbares.
          await this.saveAndExit();
        }
      }
    }
  }

  async finishOrder() {
    const order = this.subscriberService.currentOrder.getValue();
    if (order) {
      const response = await this.alert.presentConfirmationAlert(
        'Auftrag Abschließen',
        'Möchten Sie den Auftrag wirklich abschließen? Es können Änderungen nur durch Notizen vorgenommen werden.'
      );

      if (response) {
        const response2 = await this.alert.presentConfirmationAlert(
          'Info',
          'Änderungen sind nach abschluss nur noch mit Notizen möglich. Fortfahren?'
        );
        if (response2) {
          const orderId = order.orderId;
          order.state = 1;
          order.productsOriginal = order.products;
          await this.updateOrder(order);

          // finishOrder() darf nicht wie erfolgreich wirken, wenn der Abschluss nie am
          // Server ankam — sonst denkt der Nutzer, der Auftrag sei zu, obwohl er beim
          // naechsten Laden/Geraetewechsel wieder als offen auftaucht (z.B. weil der
          // Sync fehlschlug oder der products-Guard state serverseitig zurueckgesetzt hat).
          await this.order.syncOfflineOrders(true, 'finishOrder');

          const stillOffline = this.subscriberService.offlineOrders
            .getValue()
            .some(o => o.orderId === orderId);

          if (stillOffline) {
            const retry = await this.alert.presentConfirmationAlert(
              'Abschluss nicht bestätigt',
              'Der Auftrag konnte nicht mit dem Server synchronisiert werden — er ist eventuell noch als "offen" hinterlegt. Bitte Verbindung prüfen.\n\nTrotzdem verlassen (Abschluss wird beim nächsten Sync erneut versucht)?',
              'Trotzdem verlassen',
              'Hier bleiben'
            );
            if (!retry) {
              order.state = 0;
              await this.updateOrder(order);
              return;
            }
          }

          this.saveAndExit();
        }
      }

    }
  }

  async reopenOrder() {
    const order = this.subscriberService.currentOrder.getValue();
    if (!order) {
      return;
    }

    const confirmed = await this.alert.presentConfirmationAlert(
      'Auftrag wieder öffnen',
      'Soll dieser Auftrag wieder geöffnet werden? Dadurch können Agenten den Auftrag erneut bearbeiten.'
    );

    if (!confirmed) {
      return;
    }

    // Reopen
    order.state = 0;

    // If we have an original product snapshot, restore it as base.
    if ((order as any).productsOriginal) {
      try {
        order.products = structuredClone((order as any).productsOriginal);
      } catch {
        order.products = JSON.parse(JSON.stringify((order as any).productsOriginal));
      }
    }

    await this.updateOrder(order);
    this.alert.presentToast('Auftrag wurde wieder geöffnet.');
  }

  async changeInfo() {
    const order = this.subscriberService.currentOrder.getValue();
    if (order) {
      const response = await this.alert.presentTextInputAlert(
        'Daten ändern',
        'Notiz eingeben:',
        order.note || '',
        'Speichern',
        'Abbrechen'
      );

      if (response !== null) {
        order.note = response;
        await this.updateOrder(order);
      }
    }
  }

  async changeEmail() {
    const order = this.subscriberService.currentOrder.getValue();
    if (order) {
      const response = await this.alert.presentTextInputAlert(
        'Daten ändern',
        'Kontakt E-Mail eingeben:',
        order.customerEmail || '',
        'Speichern',
        'Abbrechen'
      );

      if (response !== null) {
        order.customerEmail = response;
        await this.updateOrder(order);
      }
    }
  }

  /**
   * Generische Funktion zum Ändern von Order-Properties mit Text-Input-Dialog
   * @param propertyName - Name der zu ändernden Property (z.B. 'deliveryNote', 'note', 'deliveryRef')
   * @param title - Titel für den Dialog
   * @param label - Label für das Eingabefeld
   * @param confirmButtonText - Text für den Bestätigen-Button (Standard: 'Speichern')
   * @param cancelButtonText - Text für den Abbrechen-Button (Standard: 'Abbrechen')
   * @returns true wenn gespeichert, false wenn abgebrochen
   */
  async updateOrderProperty(
    propertyName: keyof Order,
    title: string,
    label: string,
    confirmButtonText: string = 'Speichern',
    cancelButtonText: string = 'Abbrechen'
  ): Promise<boolean> {
    const order = this.subscriberService.currentOrder.getValue();
    if (!order) return false;

    const currentValue = (order[propertyName] as string) || '';
    const response = await this.alert.presentTextInputAlert(
      title,
      label,
      currentValue,
      confirmButtonText,
      cancelButtonText
    );

    if (response !== null) {
      order[propertyName] = response as any;
      await this.updateOrder(order);
      return true;
    }
    return false;
  }

  async changeAgentNote() {
    return this.updateOrderProperty(
      'agentNote',
      'Daten ändern',
      'Agenturinfo eingeben:'
    );
  }

  async changeCompany() {
    return this.updateOrderProperty(
      'companyName',
      'Daten ändern',
      'Kundenname eingeben:'
    );
  }

  async changeDeliveryNote() {
    return this.updateOrderProperty(
      'deliveryNote',
      'Daten ändern',
      'Liefernotiz eingeben:'
    );
  }

  async changeDeliveryRef() {
    return this.updateOrderProperty(
      'deliveryRef',
      'Daten ändern',
      'Lieferreferenz:'
    );
  }

  /**
   * Zentraler Änderungs-Pfad für JEDE Art von Order-Änderung (Produkte, Titel, Notiz,
   * Kontakt, Adresse, Status, ...). Speichert sofort lokal, ohne Debounce — jede hier
   * ankommende Änderung ist bereits ein abgeschlossenes Event (Klick, Dialog-Bestätigung),
   * kein Tastendruck-Strom, daher kein Verzögerungs-/Spam-Risiko. Setzt zusätzlich den
   * Inaktivitäts-Autosave-Timer zurück.
   */
  async updateOrder(order: Order) {
    // Erstelle eine neue Referenz, damit Angular's Change Detection die Änderung erkennt
    const updatedOrder = { ...order };
    this.subscriberService.setCurrentOrder(updatedOrder);

    await this.presaveOrder();
    this.resetInactivityTimer();
  }

  private resetInactivityTimer(): void {
    this.stopInactivityTimer();
    this.inactivityTimer = setTimeout(() => {
      this.inactivityTimer = null;
      void this.onInactivityTimeout();
    }, this.inactivityTimeoutMs);
  }

  private stopInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private async onInactivityTimeout(): Promise<void> {
    const order = this.subscriberService.currentOrder.getValue();
    if (!order || order.sync === true) {
      return; // nichts zu tun — bereits synchronisiert oder keine offene Order
    }

    // syncOfflineOrders() überspringt standardmäßig die aktuell offene Order — hier
    // gezielt mit forceAll erzwingen, da genau das der Zweck dieses Autosaves ist.
    await this.order.syncOfflineOrders(true, 'inactivity');
  }

  /** Stellt sicher, dass der aktuelle Stand sofort lokal persistiert ist — kein Debounce mehr,
   * daher meist ein No-Op; bleibt für explizite Save/Exit-Aufrufer als Sicherheitsnetz bestehen. */
  flushPresave() {
    this.stopInactivityTimer();
    void this.presaveOrder();
  }
}
